import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { LightLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { priceFormatterForceNegative } from '@elab/core/utils/PriceUtils';
import { actions, selectors } from '@elab/core/store/payment';
import { KLARNA_CONTAINER_ID } from '@elab/core/store/payment/klarnaHelpers';
import { selectors as cartSelectors } from '@elab/core/store/cart';
import { selectors as configSelectors } from '@elab/core/store/config';
import { evalCartType } from '@elab/core/utils/cart/CartUtils';
import { paymentConstants, paymentErrors, paymentSlots } from '@elab/core/utils/constants';
import { actions as uiActions } from '@elab/core/store/ui';
import isEmpty from '@elab/core/utils/isEmpty';

/**
 * Component is used to show available payment methods from payment store
 *
 * HTML Structure Example
 *
 * <e2-payment-methods>
 *      <e2-gift-card-form></e2-gift-card-form>
 * </e2-payment-methods>
 */

class PaymentMethods extends LightLitElement {
    static get properties() {
        return {
            methods: { type: Array },
            selectedMethod: { type: Object },
            showGifCard: { type: Boolean },
            isLoyaltyCart: { type: Boolean },
            showContent: { type: Boolean },
            showPaymentInfo: { type: Boolean },
            showSopMethod: { type: Boolean, attribute: 'show-sop-method' },
            cardListLabel: { type: String, attribute: 'card-list-label' },
            newCardLabel: { type: String, attribute: 'new-card-label' },
            saveCardLabel: { type: String, attribute: 'save-card-label' },
            saveNewCreditCardLabel: { type: String, attribute: 'save-new-creditcard-label' },
            saveCreditCardLabel: { type: String, attribute: 'save-creditcard-label' },
            payNewCardLabel: { type: String, attribute: 'pay-new-card-label' },
            sessionExpiredLabel: { type: String, attribute: 'iframe-session-expired-label' },
            isPreselectedMethod: {
                type: Boolean,
                attribute: 'preselected-method',
            },
            paymentAvailable: { type: Boolean },
            hasBillingAddress: { type: Boolean },
            resetTerms: {
                type: Boolean,
                attribute: 'reset-terms',
            },
            cardHolderPlaceHolderLabel: {
                type: String,
                attribute: 'cardholder-placeholder-label',
            },
            cardNumberPlaceHolderLabel: {
                type: String,
                attribute: 'cardnumber-placeholder-label',
            },
            cardCVCPlaceHolderLabel: {
                type: String,
                attribute: 'cvc-placeholder-label',
            },
            paypalLabelText: { type: String, attribute: 'paypal-text-label' },
            creditLabelText: { type: String, attribute: 'credit-text-label' },
            epsLabelText: { type: String, attribute: 'eps-text-label' },
            invoiceLabelText: { type: String, attribute: 'invoice-text-label' },
            twintLabelText: { type: String, attribute: 'twint-text-label' },
            cashOnDeliveryLabelText: {
                type: String,
                attribute: 'cod-text-label',
            },
            noSaveCardLabel: {
                type: String,
                attribute: 'no-save-card-label',
            },
            cardErrorText: {
                type: String,
                attribute: 'card-error-text',
            },
            cardListNotAvailableText: {
                type: String,
                attribute: 'card-list-not-available-text',
            },
            cardExpiryText: {
                type: String,
                attribute: 'card-expiry-text',
            },
            analyticsConfig: { type: String, attribute: 'analytics-config' },
            forceSyncSelection: { type: Boolean, attribute: 'force-sync-selection' },
            paymentMethodSet: { type: Boolean, attribute: false },
            checkIfMethodsEnabled: { type: Boolean, attribute: 'check-if-methods-enabled' },
            isNewOrderApiEnabled: { type: Boolean, attribute: 'is-new-order-api-enabled' },
            showDescription: { type: Boolean, attribute: 'show-description' },
            showKlarnaText: { type: Boolean, attribute: 'show-klarna-text' },
            isTokenization: { type: Boolean, attribute: false },
            klarnaShippingCostLineName: {
                type: String,
                attribute: 'klarna-shipping-cost-line-name',
            },
        };
    }

    constructor() {
        super();
        this.methods = [];
        this.price = '';
        this.selectedMethod = {};
        this.cardList = [];
        this.cardListLabel = '';
        this.newCardLabel = '';
        this.saveCardLabel = '';
        this.saveNewCreditCardLabel = '';
        this.saveCreditCardLabel = '';
        this.payNewCardLabel = '';
        this.showGifCard = false;
        this.showSopMethod = true;
        this.isLoyaltyCart = false;
        this.showContent = false;
        this.cardHolderPlaceHolderLabel = '';
        this.cardNumberPlaceHolderLabel = '';
        this.cardCVCPlaceHolderLabel = '';
        this.showPaymentInfo = false;
        this.isPreselectedMethod = false;
        this.paymentAvailable = true;
        this.resetTerms = false;
        this.analyticsConfig = '';
        this.cardExpiryText = '';
        this.hasBillingAddress = true;
        this.checkIfMethodsEnabled = false;
        this.isNewOrderApiEnabled = false;
        this.showDescription = false;
        this.klarnaShippingCostLineName = '{0} shipping cost';
        this.isTokenization = false;
        this.showKlarnaText = false;
    }

    firstUpdated() {
        this.dispatchEvent(new CustomEvent('set-is-payment-page', { detail: true }));
        if (this.isNewOrderApiEnabled) {
            this.dispatchEvent(new CustomEvent('set-is-new-order-api-enabled', { detail: true }));
        }
    }

    afterConnect() {
        this.dispatchEvent(
            new CustomEvent('get-payment-methods', {
                sopEnabled: this.showSopMethod,
            }),
        );
    }

    beforeDisconnect() {
        this.dispatchEvent(new CustomEvent('clear-payment-session'));
        this.dispatchEvent(new CustomEvent('reset-payment-page-data'));
    }

    handleClick(method) {
        const isPaymentDisabled = !this.paymentAvailable || !this.isEligible();
        if (this.checkIfMethodsEnabled && isPaymentDisabled) return;
        if (method.code === this.selectedMethod.code) return;
        this.dispatchEvent(
            new CustomEvent('enable-pay-button', {
                detail: true,
            }),
        );
        if (!this.hasBillingAddress) {
            this.dispatchEvent(new CustomEvent('display-billing-error'));
            return;
        }
        this.dispatchEvent(new CustomEvent('set-payment-method', { detail: method }));
    }

    isOfflineMode(method) {
        return method.integrationType.toLowerCase() === paymentSlots.OFFLINE;
    }

    isHopAvailable(method) {
        const isRedirect = method.integrationType === paymentSlots.REDIRECT;
        return (
            this.canSaveCard &&
            isRedirect &&
            (this.isMonetaWebCardMethod(method) || this.isSaferpayCardMethod(method)) &&
            this.isTokenization
        );
    }

    isMonetaWebCardMethod(method) {
        return method.code.toLowerCase().includes(paymentSlots.MONETAWEB);
    }

    isSaferpayCardMethod(method) {
        return method.code.toLowerCase().includes(paymentSlots.SAFERPAY_CARD);
    }

    getRadioButtonClasses(method) {
        return classMap({
            'radio-field': true,
            'radio-field--checked':
                method.code === this.selectedMethod.code &&
                (!this.forceSyncSelection || this.paymentMethodSet),
            'radio-field--disabled': !this.paymentAvailable || !this.isEligible(),
        });
    }

    getContentDescription(method) {
        const methodName = method.code.toLowerCase();
        switch (true) {
            case methodName.includes(paymentSlots.PAYPAL):
                return this.paypalLabelText;
            case methodName.includes(paymentSlots.CREDIT):
                return this.creditLabelText;
            case methodName.includes(paymentSlots.EPS):
                return this.epsLabelText;
            case methodName.includes(paymentSlots.TWINT):
                return this.twintLabelText;
            case methodName.includes(paymentSlots.MCOD):
            case methodName.includes(paymentSlots.INVOICE):
                return this.isOfflineMode(method) && !methodName.includes(paymentSlots.MCOD)
                    ? `${this.cashOnDeliveryLabelText.replace('{0}', method.name)}`
                    : `${this.invoiceLabelText.replace('{0}', method.name)}`;
            case methodName.includes(paymentSlots.KLARNA):
                this.dispatchEvent(
                    new CustomEvent('set-payment-session-delivery-line', {
                        detail: this.klarnaShippingCostLineName,
                    }),
                );
                return html`
                    ${this.showKlarnaText ? this.invoiceLabelText.replace('{0}', method.name) : ''}
                    ${this.klarnaLoadContent()}
                `;
            default:
                return html``;
        }
    }

    klarnaLoadContent() {
        return html`
            <div id="${KLARNA_CONTAINER_ID}"></div>
        `;
    }

    getTransactionCost(method) {
        let cost = '0';
        let definedCost = 0;
        if (method.paymentModeValues.length > 0) {
            definedCost = method.paymentModeValues[0].value;
            cost = priceFormatterForceNegative(
                definedCost,
                method.paymentModeValues[0].currencyIso,
                false,
            );
        }

        if (definedCost > 0) {
            cost = `(+ ${cost})`;
        } else if (definedCost < 0) {
            cost = `(- ${cost})`;
        } else {
            cost = '';
        }
        return `${cost}`;
    }

    isEligible() {
        return this.price > 0;
    }

    isSopAvailable(method) {
        return method.integrationType.toUpperCase() === paymentSlots.SEAMLESS;
    }

    canDisplaySopMethod(method) {
        const isCard = method.code.toLowerCase().includes(paymentSlots.CREDIT);
        const isMethodSelected = method.code === this.selectedMethod.code;
        return isMethodSelected && isCard && this.showSopMethod && this.isSopAvailable(method);
    }

    canShowContent(method) {
        return this.showContent && method.code === this.selectedMethod.code;
    }

    stringToHTML = str => {
        const dom = document.createElement('div');
        dom.classList.add('radio-field__description');
        dom.innerHTML = str;
        return dom;
    };

    showPaymentContent(method) {
        return this.canShowContent(method)
            ? this.isHopAvailable(method)
                ? this.renderHopMethods(method)
                : this.canDisplaySopMethod(method)
                ? this.renderSopMethods()
                : this.renderTextContent(method)
            : null;
    }

    renderTextContent(method) {
        return html`
            <div class="radio-field__row">
                <div class="radio-field__row--label">
                    ${this.getContentDescription(method)}
                </div>
            </div>
        `;
    }

    renderHopMethods(method) {
        return html`
            <div class="radio-field__row">
                <div class="radio-field__container radio-field__card__container">
                    <e2-hop-payment-methods
                        class="radio-field__row__SOP"
                        card-list-label="${this.cardListLabel}"
                        new-card-label="${this.newCardLabel}"
                        save-card-label="${this.saveCardLabel}"
                        card-error-text="${this.cardErrorText}"
                        save-new-creditcard-label="${this.saveNewCreditCardLabel}"
                        save-creditcard-label="${this.saveCreditCardLabel}"
                        pay-new-card-label="${this.payNewCardLabel}"
                        iframe-session-expired-label="${this.sessionExpiredLabel}"
                        no-save-card-label="${this.noSaveCardLabel}"
                        card-list-not-available-text="${this.cardListNotAvailableText}"
                        cardholder-placeholder-label="${this.cardHolderPlaceHolderLabel}"
                        cardnumber-placeholder-label="${this.cardNumberPlaceHolderLabel}"
                        cvc-placeholder-label="${this.cardCVCPlaceHolderLabel}"
                        card-expiry-text="${this.cardExpiryText}"
                        analytics-config=${this.analyticsConfig}
                        ?moneta-web-card-method=${this.isMonetaWebCardMethod(method)}
                        ?saferpay-card-method=${this.isSaferpayCardMethod(method)}
                    >
                    </e2-hop-payment-methods>
                </div>
            </div>
        `;
    }

    renderSopMethods() {
        return html`
            <div class="radio-field__row">
                <div class="radio-field__container radio-field__card__container">
                    <e2-sop-payment-methods
                        class="radio-field__row__SOP"
                        card-list-label="${this.cardListLabel}"
                        new-card-label="${this.newCardLabel}"
                        save-card-label="${this.saveCardLabel}"
                        card-error-text="${this.cardErrorText}"
                        iframe-session-expired-label="${this.sessionExpiredLabel}"
                        no-save-card-label="${this.noSaveCardLabel}"
                        card-list-not-available-text="${this.cardListNotAvailableText}"
                        cardholder-placeholder-label="${this.cardHolderPlaceHolderLabel}"
                        cardnumber-placeholder-label="${this.cardNumberPlaceHolderLabel}"
                        cvc-placeholder-label="${this.cardCVCPlaceHolderLabel}"
                        card-expiry-text="${this.cardExpiryText}"
                        analytics-config=${this.analyticsConfig}
                    >
                    </e2-sop-payment-methods>
                </div>
            </div>
        `;
    }

    renderRadioButton(method) {
        return html`
            <label
                class="${this.getRadioButtonClasses(method)}"
                @click="${() => this.handleClick(method)}"
                for="${method.code}"
            >
                <div class="radio-field__row">
                    <div class="radio-field__row-item">
                        <div class="radio-field__label_payment" ?hidden="${!this.showPaymentInfo}">
                            ${`${this.getTransactionCost(method)}`}
                        </div>
                        <div class="radio-field__circle">
                            <span class="radio-field__mark"></span>
                            <input
                                class="radio-field__input a11y-form__radio"
                                type="radio"
                                value="${method.code}"
                                ?checked="${method.code === this.selectedMethod.code}"
                                ?disabled="${!this.paymentAvailable || !this.isEligible()}"
                            />
                        </div>
                    </div>
                    <div class="radio-field__row-item">
                        <div class="radio-field__container radio-field__payment">
                            <span class="radio-field__container__label">${method.name}</span>
                            ${method.logo
                                ? html`
                                      <img
                                          class="payment-methods__logo"
                                          src="${method.logo ? method.logo.url : ''}"
                                          alt="${method.name}"
                                      />
                                  `
                                : ''}
                        </div>
                    </div>
                    ${this.showDescription && method.description
                        ? this.stringToHTML(method.description)
                        : ''}
                </div>
                ${this.showPaymentContent(method)}
            </label>
        `;
    }

    render() {
        return this.isLoyaltyCart
            ? html``
            : html`
                  <div class="payment-methods">
                      <div
                          class="radio-group ${this.displayMode === 'buttons'
                              ? 'radio-group--as-buttons'
                              : ''} ${this.cssClass ? this.cssClass : ''}"
                      >
                          ${this.methods
                              ? this.methods.map(method =>
                                    this.analyticsConfig !== '' && !this.canDisplaySopMethod(method)
                                        ? html`
                                              <e2-click-tracking
                                                  analytics-config="${this.analyticsConfig}"
                                                  type="payment_method-${method.code}"
                                              >
                                                  ${this.renderRadioButton(method)}
                                              </e2-click-tracking>
                                          `
                                        : this.renderRadioButton(method),
                                )
                              : null}
                      </div>
                  </div>
                  <div ?hidden=${!this.showGifCard}>
                      <v-slot name="giftcard"></v-slot>
                  </div>
              `;
    }
}

const mapStateToProps = state => {
    const methods = selectors.getMethods(state);
    const cart = cartSelectors.getData(state);
    const totalPrice = cart.totalPrice || 0;
    const paymentAvailable = cart.paymentAvailable;
    const hasBillingAddress = !!(cart.paymentAddress && !isEmpty(cart.paymentAddress));
    return {
        methods: methods.filter(
            ({ code }) =>
                !code.toLowerCase().includes(paymentConstants.GIFTCARD.toLowerCase()) &&
                !code.toLowerCase().includes(paymentConstants.PWP.toLowerCase()) &&
                !code.toLowerCase().includes(paymentConstants.Loyalty.toLowerCase()) &&
                !code.toLowerCase().includes(paymentConstants.SUPERUP.toLowerCase()),
        ),
        price: totalPrice.value,
        canSaveCard: selectors.getTokenizationAllowed(state),
        isTokenization: configSelectors.isTokenization(state),
        selectedMethod: selectors.getSelectedMethod(state),
        showGifCard: !!methods.find(({ code }) => code === 'giftcard'),
        ...evalCartType(cart.cartType, totalPrice),
        paymentAvailable,
        hasBillingAddress,
        paymentMethodSet: selectors.getPaymentMethodSet(state),
    };
};

const mapDispatchToEvents = (dispatch, ctx) => {
    return {
        'get-payment-methods': sopEnabled => {
            if (ctx.resetTerms) {
                dispatch(actions.setTermsAccepted(false));
            }
            dispatch(
                actions.getPaymentMethods({
                    options: {
                        isPreselectedMethod: ctx.isPreselectedMethod,
                        sopEnabled,
                    },
                }),
            );
        },
        'set-payment-method': e => {
            dispatch(actions.setPaymentMethod(e.detail));
        },
        'clear-payment-session': () => {
            dispatch(actions.clearPaymentSession());
        },
        'set-payment-session-delivery-line': e => {
            dispatch(actions.setPaymentSessionDeliveryLine(e.detail));
        },
        'enable-pay-button': e => dispatch(actions.payNowActive(!e.detail)),
        'set-is-payment-page': e => dispatch(actions.setIsPaymentPage(e.detail)),
        'reset-payment-page-data': () => dispatch(actions.resetPaymentPageData()),
        'display-billing-error': () => {
            dispatch(uiActions.scrollToTopOfPage());
            dispatch(actions.setError(paymentErrors.billingAddressNotSelected));
        },
        'set-is-new-order-api-enabled': e => dispatch(actions.setIsNewOrderApiEnabled(e.detail)),
    };
};

export default () =>
    customElements.define(
        'e2-payment-methods',
        connect(mapStateToProps, mapDispatchToEvents)(PaymentMethods),
    );